import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, X, Users, CalendarCheck, DollarSign, Calendar,
  BarChart2, Settings, MessageSquare, Camera, UserPlus, Briefcase,
  FileText, ClipboardList, TrendingUp, Building2, ShieldCheck,
  ArrowRight, Clock, ChevronRight, CalendarDays, ArrowRightLeft, Timer,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useEmployees } from '@/api/queries/employee.queries';
import { useDepartments } from '@/api/queries/employee.queries';
import { useLeaveRequests } from '@/api/queries/leave.queries';

// ─── Static route catalogue ──────────────────────────────────────────────────

const ROUTES = [
  // Main nav
  { id: 'r-dash',       label: 'Dashboard',            path: '/dashboard',                    icon: BarChart2,     category: 'Navigation', keywords: 'home analytics overview kpi' },
  { id: 'r-emp',        label: 'Employees',            path: '/employees',                    icon: Users,         category: 'Navigation', keywords: 'staff team headcount' },
  { id: 'r-emp-new',    label: 'Add New Employee',     path: '/employees/new',                icon: UserPlus,      category: 'Quick Action', keywords: 'create hire onboard new employee' },
  { id: 'r-att',        label: 'Attendance',           path: '/attendance',                   icon: CalendarCheck, category: 'Navigation', keywords: 'check-in punch daily live mark' },
  { id: 'r-att-qr',     label: 'Attendance QR Code',  path: '/attendance/qr',                icon: Camera,        category: 'Navigation', keywords: 'qr scan kiosk' },
  { id: 'r-payroll',    label: 'Payroll',              path: '/payroll',                      icon: DollarSign,    category: 'Navigation', keywords: 'salary salary run payslip wages' },
  { id: 'r-payroll-run',label: 'Run Payroll',          path: '/payroll/run',                  icon: DollarSign,    category: 'Quick Action', keywords: 'process payroll run wages' },
  { id: 'r-leave',      label: 'Leave Management',     path: '/leave',                        icon: Calendar,      category: 'Navigation', keywords: 'leave approval request balance' },
  { id: 'r-leave-cal',  label: 'Leave Calendar',       path: '/leave/calendar',               icon: Calendar,      category: 'Navigation', keywords: 'leave calendar view' },
  { id: 'r-recruit',    label: 'Recruitment',          path: '/recruitment',                  icon: Briefcase,     category: 'Navigation', keywords: 'hire job candidate applicant ats pipeline' },
  { id: 'r-recruit-new',label: 'Post New Job',         path: '/recruitment/new',              icon: Briefcase,     category: 'Quick Action', keywords: 'job posting opening vacancy' },
  { id: 'r-reports',    label: 'Reports',              path: '/reports',                      icon: FileText,      category: 'Navigation', keywords: 'report attendance payroll export excel pdf' },
  { id: 'r-chat',       label: 'Chat',                 path: '/chat',                         icon: MessageSquare, category: 'Navigation', keywords: 'message broadcast team communication' },
  { id: 'r-chat-bcast', label: 'Broadcast Message',   path: '/chat/broadcast',               icon: MessageSquare, category: 'Quick Action', keywords: 'announce broadcast message all' },
  { id: 'r-lens',       label: 'Lens Devices',         path: '/lens',                         icon: Camera,        category: 'Navigation', keywords: 'kiosk device selfie lens face' },
  { id: 'r-visitors',   label: 'Visitor Log',          path: '/lens/visitors',                icon: ClipboardList, category: 'Navigation', keywords: 'visitor guest log entry' },
  { id: 'r-roster',     label: 'Roster & Shifts',      path: '/roster',                       icon: Clock,         category: 'Navigation', keywords: 'shift roster schedule assign cycle' },
  { id: 'r-expense',    label: 'Financial Management', path: '/expense',                      icon: TrendingUp,    category: 'Navigation', keywords: 'expense khata advance department finance' },
  { id: 'r-compliance', label: 'Compliance',           path: '/compliance',                   icon: ShieldCheck,   category: 'Navigation', keywords: 'pf esic tds form16 compliance statutory' },
  { id: 'r-settings',   label: 'Settings',             path: '/settings',                     icon: Settings,      category: 'Navigation', keywords: 'configuration company policy account' },
  { id: 'r-settings-a', label: 'Account Settings',    path: '/settings/account',             icon: Settings,      category: 'Navigation', keywords: 'profile account password' },
  { id: 'r-id-cards',   label: 'ID Cards & Info',       path: '/employees/id-cards',           icon: Users,         category: 'Quick Action', keywords: 'id card print employee badge' },
  // Punch & Attendance Analytics (dedicated pages)
  { id: 'rpt-monthly',  label: 'Monthly Attendance Report',   path: '/reports/monthly',                 icon: CalendarDays,   category: 'Reports', keywords: 'monthly attendance calendar grid report' },
  { id: 'rpt-swipe',    label: 'Swipe / Punch Report',        path: '/reports/swipe',                   icon: ArrowRightLeft, category: 'Reports', keywords: 'swipe punch biometric log report' },
  { id: 'rpt-att',      label: 'Attendance Deep Report',      path: '/reports/attendance',              icon: ClipboardList,  category: 'Reports', keywords: 'attendance detailed filter report' },
  { id: 'rpt-strength', label: 'Strength Report',             path: '/reports/strength',                icon: Building2,      category: 'Reports', keywords: 'strength department headcount donut report' },
  { id: 'rpt-ot',       label: 'OT / Overtime Report',        path: '/reports/ot',                      icon: Timer,          category: 'Reports', keywords: 'overtime ot report hours extra' },
  { id: 'rpt-active',   label: 'Active Employee Report',      path: '/reports/active-employees',        icon: Users,          category: 'Reports', keywords: 'active employees attendance percentage report' },
  // Standard reports (navigate to /reports page pre-selected)
  { id: 'rpt-summary',  label: 'Attendance Summary Report',   path: '/reports?type=attendance_summary', icon: FileText,       category: 'Reports', keywords: 'attendance summary department report generate' },
  { id: 'rpt-detail',   label: 'Detailed Attendance Register',path: '/reports?type=attendance_detail',  icon: FileText,       category: 'Reports', keywords: 'attendance register punch detail report' },
  { id: 'rpt-late',     label: 'Late Arrivals Report',        path: '/reports?type=late_arrivals',      icon: FileText,       category: 'Reports', keywords: 'late arrival tardy report' },
  { id: 'rpt-payroll',  label: 'Payroll Summary Report',      path: '/reports?type=payroll_summary',    icon: FileText,       category: 'Reports', keywords: 'payroll salary summary report generate' },
  { id: 'rpt-leave-b',  label: 'Leave Balance Report',        path: '/reports?type=leave_balance',      icon: FileText,       category: 'Reports', keywords: 'leave balance remaining report generate' },
  { id: 'rpt-leave-h',  label: 'Leave History Report',        path: '/reports?type=leave_history',      icon: FileText,       category: 'Reports', keywords: 'leave history applications status report' },
];

const QUICK_ACTIONS = ROUTES.filter(r => r.category === 'Quick Action').slice(0, 4);

// ─── Category filter config ───────────────────────────────────────────────────

const CATEGORIES = [
  { id: 'all',         label: 'All',         icon: Search },
  { id: 'employees',   label: 'Employees',   icon: Users },
  { id: 'departments', label: 'Departments', icon: Building2 },
  { id: 'leave',       label: 'Leave',       icon: Calendar },
  { id: 'navigation',  label: 'Pages',       icon: ArrowRight },
  { id: 'reports',     label: 'Reports',     icon: FileText },
];

// ─── Scoring & search ─────────────────────────────────────────────────────────

function score(haystack, needle) {
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  if (h.startsWith(n)) return 3;
  if (h.includes(n)) return 2;
  const words = n.split(/\s+/);
  if (words.every(w => h.includes(w))) return 1;
  return 0;
}

function matchRoute(route, q) {
  const h = `${route.label} ${route.keywords || ''} ${route.path}`;
  return score(h, q);
}

function matchEmp(emp, q) {
  const name = `${emp.first_name || ''} ${emp.last_name || ''}`.trim();
  const h = `${name} ${emp.employee_code || ''} ${emp.department?.name || emp.department || ''} ${emp.designation?.name || emp.designation || ''}`;
  return score(h, q);
}

function matchDept(dept, q) {
  return score(`${dept.name || ''} ${dept.description || ''}`, q);
}

function matchLeave(req, q) {
  const empName = req.employee?.name || '';
  const h = `${empName} ${req.leave_type || ''} ${req.status || ''} ${req.from_date || ''}`;
  return score(h, q);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ResultItem({ icon: Icon, label, sublabel, badge, badgeColor, index, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      data-result-index={index}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-2.5 transition-colors text-left',
        isActive ? 'bg-[var(--accent)]/10' : 'hover:bg-[var(--bg-hover)]',
      )}
    >
      <div className={cn(
        'w-8 h-8 rounded-[8px] flex items-center justify-center shrink-0',
        isActive ? 'bg-[var(--accent)]/15 text-[var(--accent)]' : 'bg-[var(--bg-muted)] text-[var(--text-secondary)]',
      )}>
        <Icon size={15} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-[var(--text-primary)] truncate">{label}</div>
        {sublabel && <div className="text-[11px] text-[var(--text-tertiary)] truncate mt-0.5">{sublabel}</div>}
      </div>
      {badge && (
        <span className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0', badgeColor || 'bg-[var(--bg-muted)] text-[var(--text-secondary)]')}>
          {badge}
        </span>
      )}
      <ChevronRight size={13} className="text-[var(--text-tertiary)] shrink-0" />
    </button>
  );
}

function SectionHeader({ label }) {
  return (
    <div className="px-4 pt-3 pb-1">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-[var(--text-tertiary)]">{label}</span>
    </div>
  );
}

const STATUS_BADGE = {
  PENDING:  { color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',  label: 'Pending' },
  APPROVED: { color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400', label: 'Approved' },
  REJECTED: { color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400', label: 'Rejected' },
};

// ─── Main component ───────────────────────────────────────────────────────────

export function CommandPalette({ open, onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Data sources — prefetch on open so results are instant
  const { data: empData } = useEmployees({ limit: 500 });
  const { data: deptData } = useDepartments();
  const { data: leaveData } = useLeaveRequests({ limit: 100 });

  const employees = useMemo(() => empData?.data || [], [empData]);
  const departments = useMemo(() => deptData?.data || [], [deptData]);
  const leaveRequests = useMemo(() => leaveData?.data || [], [leaveData]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setCategory('all');
      setActiveIndex(0);
    }
  }, [open]);

  // Build result list
  const results = useMemo(() => {
    const q = query.trim();
    const items = [];

    if (!q) {
      // Empty state: show quick actions
      return QUICK_ACTIONS.map(r => ({
        type: 'route',
        id: r.id,
        label: r.label,
        sublabel: r.path,
        icon: r.icon,
        action: r.path,
        section: 'Quick Actions',
      }));
    }

    // Routes/Navigation
    if (category === 'all' || category === 'navigation') {
      const matched = ROUTES
        .filter(r => r.category !== 'Reports')
        .map(r => ({ ...r, _score: matchRoute(r, q) }))
        .filter(r => r._score > 0)
        .sort((a, b) => b._score - a._score)
        .slice(0, 5);
      if (matched.length) {
        matched.forEach(r => items.push({ type: 'route', id: r.id, label: r.label, sublabel: r.path, icon: r.icon, action: r.path, section: 'Pages' }));
      }
    }

    // Reports
    if (category === 'all' || category === 'reports') {
      const matched = ROUTES
        .filter(r => r.category === 'Reports')
        .map(r => ({ ...r, _score: matchRoute(r, q) }))
        .filter(r => r._score > 0)
        .sort((a, b) => b._score - a._score)
        .slice(0, category === 'reports' ? 10 : 4);
      if (matched.length) {
        matched.forEach(r => items.push({ type: 'route', id: r.id, label: r.label, sublabel: 'Report', icon: r.icon, action: r.path, section: 'Reports' }));
      }
    }

    // Employees
    if (category === 'all' || category === 'employees') {
      const matched = employees
        .map(e => ({ ...e, _score: matchEmp(e, q) }))
        .filter(e => e._score > 0)
        .sort((a, b) => b._score - a._score)
        .slice(0, 6);
      if (matched.length) {
        matched.forEach(e => {
          const name = `${e.first_name || ''} ${e.last_name || ''}`.trim();
          const dept = e.department?.name || e.department || '';
          const desig = e.designation?.name || e.designation || '';
          items.push({
            type: 'employee',
            id: e.id,
            label: name,
            sublabel: [e.employee_code, dept, desig].filter(Boolean).join(' · '),
            icon: Users,
            badge: e.status === 'ACTIVE' ? 'Active' : e.status,
            badgeColor: e.status === 'ACTIVE'
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400'
              : 'bg-[var(--bg-muted)] text-[var(--text-secondary)]',
            action: `/employees/${e.id}`,
            section: 'Employees',
          });
        });
      }
    }

    // Departments
    if (category === 'all' || category === 'departments') {
      const matched = departments
        .map(d => ({ ...d, _score: matchDept(d, q) }))
        .filter(d => d._score > 0)
        .sort((a, b) => b._score - a._score)
        .slice(0, 4);
      if (matched.length) {
        matched.forEach(d => items.push({
          type: 'department',
          id: d.id,
          label: d.name,
          sublabel: `${d.employee_count || 0} employees`,
          icon: Building2,
          action: `/employees?department=${encodeURIComponent(d.name)}`,
          section: 'Departments',
        }));
      }
    }

    // Leave requests
    if (category === 'all' || category === 'leave') {
      const matched = leaveRequests
        .map(r => ({ ...r, _score: matchLeave(r, q) }))
        .filter(r => r._score > 0)
        .sort((a, b) => b._score - a._score)
        .slice(0, 4);
      if (matched.length) {
        matched.forEach(r => {
          const sb = STATUS_BADGE[r.status] || {};
          items.push({
            type: 'leave',
            id: r.id,
            label: `${r.employee?.name || 'Employee'} — ${r.leave_type?.replace(/_/g, ' ') || 'Leave'}`,
            sublabel: `${r.from_date} to ${r.to_date} · ${r.days} day(s)`,
            icon: Calendar,
            badge: sb.label || r.status,
            badgeColor: sb.color,
            action: '/leave',
            section: 'Leave Requests',
          });
        });
      }
    }

    return items;
  }, [query, category, employees, departments, leaveRequests]);

  // Group by section
  const sections = useMemo(() => {
    const map = new Map();
    results.forEach((item, idx) => {
      const sec = item.section || 'Results';
      if (!map.has(sec)) map.set(sec, []);
      map.get(sec).push({ ...item, _idx: idx });
    });
    return map;
  }, [results]);

  // Active index clamping
  const total = results.length;
  const clamp = (n) => Math.max(0, Math.min(n, total - 1));

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => clamp(i + 1)); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIndex(i => clamp(i - 1)); return; }
    if (e.key === 'Enter' && results[activeIndex]) {
      e.preventDefault();
      navigate(results[activeIndex].action);
      onClose();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, results, navigate, onClose]);

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-result-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Reset active when results change
  useEffect(() => { setActiveIndex(0); }, [results]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center pt-[10vh]" onMouseDown={onClose}>
      <div
        className="relative w-full max-w-[640px] mx-4 bg-[var(--bg-card)] border border-[var(--border)] rounded-[16px] shadow-2xl overflow-hidden"
        onMouseDown={e => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--border)]">
          <Search size={18} className="text-[var(--text-tertiary)] shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search employees, pages, leave requests..."
            className="flex-1 bg-transparent text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] outline-none"
          />
          {query && (
            <button onClick={() => setQuery('')} className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]">
              <X size={15} />
            </button>
          )}
          <kbd className="hidden sm:flex items-center gap-1 px-2 py-1 rounded-[6px] bg-[var(--bg-muted)] text-[10px] text-[var(--text-tertiary)] border border-[var(--border)] shrink-0">
            Esc
          </kbd>
        </div>

        {/* Category pills */}
        <div className="flex items-center gap-1.5 px-4 py-2 border-b border-[var(--border)] overflow-x-auto scrollbar-none">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium whitespace-nowrap transition-colors shrink-0',
                category === cat.id
                  ? 'bg-[var(--accent)] text-white'
                  : 'bg-[var(--bg-muted)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]',
              )}
            >
              <cat.icon size={12} />
              {cat.label}
            </button>
          ))}
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[400px] overflow-y-auto">
          {results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-[var(--text-tertiary)]">
              <Search size={28} className="mb-2 opacity-30" />
              <p className="text-[13px]">No results for "{query}"</p>
              <p className="text-[11px] mt-1">Try a different keyword or category</p>
            </div>
          ) : (
            Array.from(sections.entries()).map(([sectionName, items]) => (
              <div key={sectionName}>
                <SectionHeader label={sectionName} />
                {items.map(item => (
                  <ResultItem
                    key={item.id}
                    icon={item.icon}
                    label={item.label}
                    sublabel={item.sublabel}
                    badge={item.badge}
                    badgeColor={item.badgeColor}
                    index={item._idx}
                    isActive={item._idx === activeIndex}
                    onClick={() => { navigate(item.action); onClose(); }}
                  />
                ))}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-[var(--border)] flex items-center gap-4 text-[11px] text-[var(--text-tertiary)]">
          <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded bg-[var(--bg-muted)] border border-[var(--border)] text-[10px]">↑↓</kbd> navigate</span>
          <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded bg-[var(--bg-muted)] border border-[var(--border)] text-[10px]">↵</kbd> open</span>
          <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded bg-[var(--bg-muted)] border border-[var(--border)] text-[10px]">Esc</kbd> close</span>
          <span className="ml-auto">{results.length} result{results.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
